import { useState, useMemo } from 'react';
import type { RankedBrand } from '../hooks/useCompetitorScores.js';
import type { VisibilityScore } from '../types/index.js';
import { ConfidenceBadge } from './ConfidenceBadge.js';
import { reliabilityTierForSampleSize } from './EngineVisibilityCard.js';

interface RankingsTableProps {
  rankings: RankedBrand[];
  engines: Set<string>;
}

type SortField = 'name' | 'mention_rate' | 'share_of_voice' | 'avg_sentiment' | 'avg_position';
type SortDir = 'asc' | 'desc';

/** Aggregate scores across all matching engines into a single summary. */
function aggregateScores(
  scores: VisibilityScore[],
  engines: Set<string>,
): {
  mention_rate: number | null;
  share_of_voice: number | null;
  avg_sentiment: number | null;
  avg_position: number | null;
  sample_size: number;
  reliability: 'directional' | 'operational' | 'high_confidence';
} {
  const filtered = scores.filter((s) => engines.has(s.engine));
  if (filtered.length === 0) {
    return { mention_rate: null, share_of_voice: null, avg_sentiment: null, avg_position: null, sample_size: 0, reliability: 'directional' };
  }

  let totalSamples = 0;
  let weightedMR = 0;
  let weightedSOV = 0;
  let weightedSent = 0;
  let weightedPos = 0;
  let mrSamples = 0;
  let sovSamples = 0;
  let sentSamples = 0;
  let posSamples = 0;

  for (const s of filtered) {
    totalSamples += s.sample_size;
    if (s.mention_rate != null) {
      weightedMR += s.mention_rate * s.sample_size;
      mrSamples += s.sample_size;
    }
    if (s.share_of_voice != null) {
      weightedSOV += s.share_of_voice * s.sample_size;
      sovSamples += s.sample_size;
    }
    if (s.avg_sentiment != null) {
      weightedSent += s.avg_sentiment * s.sample_size;
      sentSamples += s.sample_size;
    }
    if (s.avg_position != null) {
      weightedPos += s.avg_position * s.sample_size;
      posSamples += s.sample_size;
    }
  }

  // Spec 179: the tier is a function of the POOLED sample size (mirrors backend
  // getReliabilityTier) — a pooled estimate over many responses is genuinely more
  // reliable than any single thin bucket, so n drives the tier. (Supersedes the
  // 177 worst-of, which under-claimed once windows pooled n into the thousands.)
  const tier = reliabilityTierForSampleSize(totalSamples);

  return {
    mention_rate: mrSamples > 0 ? Math.round((weightedMR / mrSamples) * 1000) / 1000 : null,
    share_of_voice: sovSamples > 0 ? Math.round((weightedSOV / sovSamples) * 1000) / 1000 : null,
    avg_sentiment: sentSamples > 0 ? Math.round((weightedSent / sentSamples) * 1000) / 1000 : null,
    avg_position: posSamples > 0 ? Math.round((weightedPos / posSamples) * 10) / 10 : null,
    sample_size: totalSamples,
    reliability: tier as 'directional' | 'operational' | 'high_confidence',
  };
}

function formatPct(v: number | null): string {
  if (v == null) return '--';
  return `${(v * 100).toFixed(1)}%`;
}

function formatNum(v: number | null): string {
  if (v == null) return '--';
  return v.toFixed(1);
}

function TrendArrow({ current, baseline }: { current: number | null; baseline: number | null }) {
  if (current == null || baseline == null) return <span className="text-gray-300">--</span>;
  const diff = current - baseline;
  if (Math.abs(diff) < 0.02) {
    return <span className="text-gray-400" title={`${(diff * 100).toFixed(1)}pp`}>--</span>;
  }
  if (diff > 0) {
    return <span className="text-green-600" title={`+${(diff * 100).toFixed(1)}pp`}>&#9650;</span>;
  }
  return <span className="text-red-600" title={`${(diff * 100).toFixed(1)}pp`}>&#9660;</span>;
}

export function RankingsTable({ rankings, engines }: RankingsTableProps) {
  const [sortField, setSortField] = useState<SortField>('mention_rate');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const rows = useMemo(() => {
    const mapped = rankings.map((r) => ({
      ...r,
      agg: aggregateScores(r.scores, engines),
    }));

    mapped.sort((a, b) => {
      if (sortField === 'name') {
        const cmp = a.name.localeCompare(b.name);
        return sortDir === 'asc' ? cmp : -cmp;
      }
      const av = a.agg[sortField] ?? -Infinity;
      const bv = b.agg[sortField] ?? -Infinity;
      return sortDir === 'asc' ? av - bv : bv - av;
    });

    return mapped;
  }, [rankings, engines, sortField, sortDir]);

  function handleSort(field: SortField) {
    if (field === sortField) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  }

  function sortIndicator(field: SortField) {
    if (field !== sortField) return '';
    return sortDir === 'asc' ? ' \u25B2' : ' \u25BC';
  }

  if (rankings.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center hub-plate--flat">
        <p className="text-sm text-gray-400">No ranking data available</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="hub-table" data-testid="rankings-table">
        <thead>
          <tr>
            {([
              ['name', 'Brand'],
              ['mention_rate', 'Mention rate'],
              ['share_of_voice', 'SOV'],
              ['avg_sentiment', 'Avg sentiment'],
              ['avg_position', 'Avg position'],
            ] as [SortField, string][]).map(([field, label]) => (
              <th
                key={field}
                className={field === 'name' ? 'cursor-pointer' : 'is-num cursor-pointer'}
                onClick={() => handleSort(field)}
              >
                {label}{sortIndicator(field)}
              </th>
            ))}
            <th>Trend</th>
            <th>Confidence</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className={row.isSelected ? 'bg-[var(--hub-color-action-soft)]' : undefined}
            >
              <td className="font-medium">
                {row.name}
                {row.isSelected && (
                  <span className="ml-1.5 text-xs text-[var(--hub-color-action-text)]">(you)</span>
                )}
              </td>
              <td className="is-num">{formatPct(row.agg.mention_rate)}</td>
              <td className="is-num">{formatPct(row.agg.share_of_voice)}</td>
              <td className="is-num">{formatNum(row.agg.avg_sentiment)}</td>
              <td className="is-num">{formatNum(row.agg.avg_position)}</td>
              <td>
                <TrendArrow current={row.agg.mention_rate} baseline={null} />
              </td>
              <td>
                <ConfidenceBadge reliability={row.agg.reliability} sampleSize={row.agg.sample_size} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
